/**
 * The printed breakdown — a document, not a screenshot of the modal.
 *
 * Pure and presentational: props in, static markup out. It is rendered with
 * `renderToStaticMarkup` into a print frame that loads NONE of the app's CSS (see
 * printStyles.js), so this file must import nothing that expects the app
 * cascade — no antd, no component stylesheets. React, dayjs, the logo asset and
 * the two shared `lib` modules are the whole dependency list, which is also what
 * lets it be tested in plain JSDOM.
 *
 * Audience (decided with the office): a record for the office to file and hand
 * to the patient. So: patient / member / office contact, then the same sheet the
 * Breakdowns tab shows — Plan details, the coverage conditions, coverage by
 * category (each maximum once) and the procedures by category (covered, how
 * often, next eligible) — and no internal workflow state (no Needs-Attention
 * note, no handled-by) and none of the office's claim-submission identifiers.
 */
import React from "react";
import dayjs from "dayjs";

import smilepassLogo from "../../../../../assets/img/smilepass-logo.png";
import {
  SECTION_ORDER, buildSheet, categoryCoverageWords, coverageConditionGroups, coverageTableRows, dollars, orthoWords, planDetailFields,
  planWideSentences, poolAmountWords, poolWhoWords,
} from "../sheet/sheetModel";
import { carrierVerbatim, fmtDate } from "./printFormat";

// Re-exported: older call sites and tests import them from here.
export { carrierVerbatim, fmtDate };

export const CATEGORY_ORDER = SECTION_ORDER;

// ---- formatting ----------------------------------------------------------

function dash(value) {
  return value == null || value === "" ? "—" : String(value);
}

function personName(first, last) {
  return [first, last].filter(Boolean).join(" ") || null;
}

function patientAddress(p) {
  const parts = [p.address, p.city, p.state, p.zip_code].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

function fileName(file) {
  if (!file) return null;
  if (file.document_name) return String(file.document_name);
  const path = String(file.document || "");
  return path.split("/").filter(Boolean).pop() || null;
}

function KV({ rows }) {
  return (
    <div className="kv">
      {rows.map(([k, v]) => (
        <React.Fragment key={k}>
          <div className="k">{k}</div>
          <div className="v">{dash(v)}</div>
        </React.Fragment>
      ))}
    </div>
  );
}

// ---- the sheet, on paper ---------------------------------------------------

// The tab's plan summary, on paper: Plan details, the coverage conditions and
// coverage by category (each maximum once). FTC's print says "Not asked" where
// a plan fact is unrecorded; the office's shows a dash and leaves out the
// unanswered conditions — the same rule as the tab.
function Facts({ fields, unknown }) {
  return (
    <div className="facts">
      {fields.map((f) => (
        <div key={f.label}>
          <div className="k">{f.label}</div>
          <div className={f.value == null ? "v muted" : "v"}>{f.value == null ? unknown : f.value}</div>
        </div>
      ))}
    </div>
  );
}

function CoverageByCategory({ table, ortho }) {
  return (
    <table className="grid coverage">
      <colgroup>
        <col style={{ width: "22%" }} />
        <col style={{ width: "22%" }} />
        <col style={{ width: "36%" }} />
        <col style={{ width: "20%" }} />
      </colgroup>
      <thead><tr><th>Category</th><th>Coverage</th><th>Maximum</th><th>Remaining</th></tr></thead>
      <tbody>
        {table.map((r) => {
          const pool = r.pool;
          const merged = r.span > 1;
          const who = merged ? poolWhoWords(pool) : r.sharedWith.length ? `Shared with ${r.sharedWith.join(" and ")}` : "";
          // The %, else the status the lines or the office gave ("Predetermination
          // required"), else a dash; a status that says more sits under the %.
          const words = categoryCoverageWords(r);
          const main = words.pct || "—";
          return (
            <tr key={r.cat}>
              <td><strong>{r.cat}</strong></td>
              <td>
                {words.pct || !words.status
                  ? (r.overridden ? `${main} (set by the office)` : main)
                  : <><strong className={`tone-${words.status.tone}`}>{words.status.label}</strong>{r.overridden ? " (set by the office)" : ""}</>}
                {words.pct && words.status ? <div className="muted small">{words.status.label}</div> : null}
                {words.note ? <div className="muted small">{words.note}</div> : null}
                {r.cat === "Orthodontics" && ortho ? <div className="muted small">{ortho}</div> : null}
              </td>
              {r.span === 0 ? null : (
                <>
                  <td rowSpan={merged ? r.span : undefined}>
                    {pool ? (
                      <>
                        {poolAmountWords(pool)}
                        {[who, pool.poolId].filter(Boolean).length ? (
                          <div className="muted small">{[who, pool.poolId].filter(Boolean).join(" · ")}</div>
                        ) : null}
                        {pool.note ? <div className="muted small">{pool.note}</div> : null}
                      </>
                    ) : "—"}
                  </td>
                  <td rowSpan={merged ? r.span : undefined}>
                    {pool && pool.remaining != null && !pool.unlimited ? dollars(pool.remaining) : "—"}
                  </td>
                </>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PlanSection({ policy, categories, sharedMaxGroups, maxResetDate, rows, intake }) {
  const answered = (rows || []).filter((r) => r && !r.data_unavailable);
  const details = planDetailFields({ policy, rows: answered, maxResetDate });
  const conditions = [].concat(...coverageConditionGroups({ policy, rows: answered }))
    .filter((f) => intake || f.value != null);
  const table = coverageTableRows({ categories, sharedMaxGroups, rows: answered, policy });
  const notes = planWideSentences(answered).notes;
  const unknown = intake ? "Not asked" : "—";
  return (
    <div className="section">
      <h2 className="section-title">Plan details</h2>
      <Facts fields={details} unknown={unknown} />
      {conditions.length ? (
        <>
          <h2 className="section-title stacked">Coverage conditions</h2>
          <Facts fields={conditions} unknown={unknown} />
        </>
      ) : null}
      <h2 className="section-title stacked">Coverage by category</h2>
      <CoverageByCategory table={table} ortho={orthoWords(policy)} />
      {notes.length ? <div className="muted small" style={{ marginTop: 6 }}>{`The carrier says of every procedure: ${notes.join(" · ")}`}</div> : null}
    </div>
  );
}

function ProceduresSection({ sheet }) {
  if (!sheet.sections.length) {
    return <p className="empty">No procedure coverage has been recorded on this breakdown.</p>;
  }
  return (
    <table className="grid procedures">
      <colgroup>
        <col style={{ width: "34%" }} />
        <col style={{ width: "15%" }} />
        <col style={{ width: "33%" }} />
        <col style={{ width: "18%" }} />
      </colgroup>
      <thead>
        <tr><th>Procedure</th><th>Covered</th><th>How often</th><th>Next eligible</th></tr>
      </thead>
      <tbody>
        {sheet.sections.map((section) => (
          <React.Fragment key={section.cat}>
            <tr className="group">
              <td colSpan={4}>
                {section.cat}
                {section.notCovered ? " · Not covered" : section.pct != null ? ` · ${section.pct}%` : ""}
                {section.pool ? <span className="muted">{` — ${section.pool}`}</span> : null}
              </td>
            </tr>
            {section.lines.map((line) => {
              const h = line.howOften;
              const often = [h.main, h.ages].filter(Boolean).join(" · ");
              const status = line.covered.status;
              return (
                <tr key={line.key}>
                  <td>
                    <strong>{line.name}</strong>{line.qualifier ? `, ${line.qualifier}` : ""}
                    <span className="code">{line.code}</span>
                    {line.also.length ? <div className="muted small">{`also ${line.also.join(", ")} · same answer`}</div> : null}
                  </td>
                  <td>
                    {status ? <strong className={`tone-${status.tone}`}>{status.label}</strong> : null}
                    {line.covered.pct != null ? <div>{`${line.covered.pct}%`}</div> : null}
                    {!status && line.covered.pct == null ? "—" : null}
                    {line.deductible ? <div className="muted small">{line.deductible}</div> : null}
                  </td>
                  <td>
                    {often ? `${often}${h.basis ? ` · ${h.basis}` : ""}` : "—"}
                    {line.shared.map((x) => <div className="shared" key={x}>{x}</div>)}
                    {line.rules.map((x) => <div className="rule" key={x}>{x}</div>)}
                  </td>
                  <td>
                    {line.next.kind === "none" ? "—" : <strong className={line.next.kind === "now" ? "tone-ok" : line.next.kind === "tooth" ? "" : "tone-warn"}>{line.next.label}</strong>}
                    {line.next.sub ? <div className="muted small">{line.next.sub}</div> : null}
                    {line.pays != null ? <div className="tone-ok small">{`Pays ${dollars(line.pays)}`}</div> : null}
                  </td>
                </tr>
              );
            })}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
}

function Unavailable({ sheet }) {
  if (!sheet.unavailable.length) return null;
  return (
    <div className="section unavailable">
      <h2 className="section-title">Not available from the carrier</h2>
      <div className="muted">
        The automatic check asked the carrier for these {sheet.unavailable.length === 1 ? "code" : "codes"} and got nothing
        back. They are not confirmed as covered or not covered.
      </div>
      <div className="codes">
        {sheet.unavailable.map((u) => (
          <div key={u.row.id != null ? u.row.id : u.code}><strong>{u.code}</strong>{` – ${u.name}`}</div>
        ))}
      </div>
    </div>
  );
}

// ---- the document ----------------------------------------------------------

export default function BreakdownPrintDocument({
  patient,
  policy,
  officeIdentity,
  categories,
  sharedMaxGroups,
  maxResetDate,
  rows,
  unavailableRows,
  files,
  printedAt,
  // FTC personnel: unrecorded plan facts print as "Not asked".
  intake = false,
}) {
  const pt = patient || {};
  const pol = policy || {};
  const office = officeIdentity || null;
  const printed = dayjs(printedAt || undefined);
  const printedLabel = printed.isValid() ? printed.format("MMM D, YYYY · h:mm A") : "";
  const asOf = printed.isValid() ? printed.format("MMM D, YYYY") : "";

  const patientName = personName(pt.first_name, pt.last_name) || "Patient";
  const carrier = pol.insurance_name_display || pol.insurance_name || null;
  const ordinal = pol.insurance_type_display || pol.insurance_type || null;
  const carrierLine = carrier ? (ordinal ? `${carrier} (${ordinal})` : carrier) : "—";

  const sheet = buildSheet({
    rows: (rows || []).concat((unavailableRows || []).filter((r) => r && r.data_unavailable)),
    categories, sharedMaxGroups, policy: pol, today: printedAt,
  });

  const sourceNames = (files || []).map(fileName).filter(Boolean);

  return (
    <table className="doc">
      <thead>
        <tr>
          <td>
            <div className="hdr">
              <div className="hdr-left">
                <img src={smilepassLogo} alt="Smilepass" />
                <div className="hdr-title">Insurance Coverage Breakdown</div>
              </div>
              <div className="hdr-right">
                <strong>{patientName}</strong>
                <br />
                {carrierLine}
                {printedLabel ? <><br />Printed {printedLabel}</> : null}
              </div>
            </div>
          </td>
        </tr>
      </thead>
      <tfoot>
        <tr>
          <td>
            <div className="ftr">
              <div>smilepass.com · support@smilepass.com</div>
              <div className="disclaimer">
                {asOf ? `Coverage as recorded on ${asOf}. ` : ""}
                Figures reflect what the carrier reported at that time and are subject to the carrier’s
                adjudication when a claim is submitted.
              </div>
            </div>
          </td>
        </tr>
      </tfoot>
      <tbody>
        <tr>
          <td>
            <div className="section">
              <div className="summary">
                <div>
                  <h2 className="section-title">Patient</h2>
                  <KV rows={[
                    ["Name", patientName],
                    ["Date of birth", fmtDate(pt.birth_date || pt.date_of_birth)],
                    ["Relation", pol.relation_to_member_display || pol.relation_to_member],
                    ["Address", patientAddress(pt)],
                    ["Phone", pt.phone],
                    ["Email", pt.email],
                  ]} />
                  <h2 className="section-title stacked">Office</h2>
                  {office ? (
                    <KV rows={[
                      ["Practice", office.office_name],
                      ["Address", office.office_address],
                      ["Phone", office.office_contact_number],
                      ["Email", office.office_email],
                    ]} />
                  ) : <div className="muted">—</div>}
                </div>
                <div>
                  <h2 className="section-title">Member</h2>
                  <div className="kv-split">
                    <KV rows={[
                      ["Carrier", carrierLine],
                      ["Plan name", pol.plan_name],
                      ["Member", personName(pol.first_name, pol.last_name)],
                    ]} />
                    <KV rows={[
                      ["Member DOB", fmtDate(pol.date_of_birth)],
                      ["Member ID", pol.member_id],
                      ["Group / contract", pol.contract_number],
                    ]} />
                  </div>
                </div>
              </div>
            </div>

            <PlanSection policy={pol} categories={categories} sharedMaxGroups={sharedMaxGroups}
              maxResetDate={maxResetDate} rows={rows} intake={intake} />

            <div className="section" style={{ breakInside: "auto", pageBreakInside: "auto" }}>
              <h2 className="section-title">Procedures</h2>
              <ProceduresSection sheet={sheet} />
            </div>

            <Unavailable sheet={sheet} />

            {sheet.finePrint.length ? (
              <div className="section muted small">
                {`Carrier fine print (on ${sheet.finePrintRows} line${sheet.finePrintRows === 1 ? "" : "s"}): ${sheet.finePrint.join(" ")}`}
              </div>
            ) : null}

            {sourceNames.length ? (
              <div className="section muted">
                <strong>Source documents:</strong> {sourceNames.join(" · ")}
              </div>
            ) : null}
          </td>
        </tr>
      </tbody>
    </table>
  );
}
